"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StateRules = exports.FederalRules = exports.JurisdictionRules = void 0;
const saaThresholds_1 = require("encamp-shared/dist/constants/saaThresholds");
/**
 * Note that these classes are meant to be singletons instantiated at module load;
 * they are classes because the partial-override ergonomics of TS classes fit the
 * domain we need to model surprisingly well.
 * They should never carry any instance state, only static constants and pure functions.
 */
/**
 * The contract a jurisdiction implements: three limit slots the concrete
 * `resolve` template composes, plus the hand-authored spec the sweep holds
 * the class to. Rules and spec travel on one object so they cannot be paired
 * wrong — see `StateRules` for what must and must not inherit.
 */
class JurisdictionRules {
    constructor() {
        /**
         * Named determinations this jurisdiction computes from raw axes (see
         * `Derivation`). Defaulted empty rather than abstract: most jurisdictions
         * derive nothing and shouldn't have to say so. The cost is that a state
         * that authors a table but forgets to override this compiles — caught at
         * test time, when the sweep's expansion throws on the first row keyed on
         * the undeclared determination.
         */
        this.derivations = {};
    }
    resolve(ctx) {
        if (ctx.areaType === "CENTRAL") {
            return { areaType: "CENTRAL", clock: this.centralClock(ctx) };
        }
        return {
            areaType: "SATELLITE",
            caps: this.satelliteCaps(ctx),
            clock: this.satelliteClock(ctx),
        };
    }
}
exports.JurisdictionRules = JurisdictionRules;
/**
 * The specification `FederalRules` is tested against. The state-status axis
 * never informs a federal determination, and no federal rule reads the
 * waste-stream classification, so no row states either.
 *
 * Numbers and citations are literal rather than imported: this table is what
 * pins the implementation's constants, so a change to one must fail here.
 */
const FEDERAL_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 3,
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
/**
 * 262.15 imposes no standing satellite clock — the only time limit is the
 * move window once a cap is breached, carried as moveWindowDays on the caps.
 */
const NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION ──────────────────────────────────────────────
    // VSQG — 40 CFR 262.14
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "VSQG",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
        },
        note: "A generator meeting the VSQG conditions of 262.14 is not subject to the " +
            "central accumulation time limits of 262.16/262.17, and 262.14 imposes none of its own. " +
            "What constrains a VSQG is the facility-wide on-site quantity cap, tracked outside this " +
            "table.",
    },
    // SQG — 40 CFR 262.16
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: false,
        },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
    },
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: true,
        },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(d)", jurisdiction: "FEDERAL" },
        },
    },
    // LQG — 40 CFR 262.17
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: "LQG",
        },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
    },
    // Unknown status
    {
        when: {
            areaType: "CENTRAL",
            generatorStatus: null,
        },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 40 CFR 262.15 ────────────────────────────
    // One row per status rather than an omission, because the VSQG carve-out
    // splits the axis.
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: "SQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: "LQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: "VSQG",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
            clock: NO_STANDING_SATELLITE_CLOCK,
        },
        note: "262.15 grants relief from the " +
            "central accumulation time limits of 262.16/262.17, which a VSQG is not subject to, and " +
            "262.14 never references 262.15 — so we read the satellite caps as not reaching VSQGs.",
    },
    {
        when: {
            areaType: "SATELLITE",
            generatorStatus: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: "The more protective default until the status is resolved.",
    },
];
/**
 * Federal RCRA generator accumulation limits, 40 CFR part 262 — and the
 * behavior every state class starts from (see `StateRules`).
 *
 * Every result stamps `jurisdiction` as a LITERAL matching the citation
 * beside it. Deliberately no `this.jurisdiction` member to DRY that up: an
 * inherited or `super.`-delegated method keeps the subclass's `this`, so a
 * federal answer reached through a state class would come back stamped with
 * the state's jurisdiction next to a CFR citation. Federal methods stamping
 * FEDERAL regardless of the instance is what keeps a state's inherited slots
 * honestly labeled.
 */
class FederalRules extends JurisdictionRules {
    constructor() {
        super(...arguments);
        this.expectations = FEDERAL_EXPECTATIONS;
    }
    centralClock(ctx) {
        if (ctx.generatorStatus === "LQG") {
            return { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "SQG") {
            if (ctx.mustTransport200Miles) {
                return { kind: "TIME", days: 270, citation: "40 CFR 262.16(d)", jurisdiction: "FEDERAL" };
            }
            return { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "VSQG") {
            // A VSQG under 262.14 is not subject to 262.16/262.17's central time
            // limits, and 262.14 imposes none of its own; what constrains a VSQG is
            // the facility-wide on-site cap, which the facility rollup owns.
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        return { kind: "UNDETERMINED" };
    }
    satelliteCaps(ctx) {
        if (ctx.generatorStatus === "VSQG") {
            // We read 262.15 as relief from central time limits a VSQG never had,
            // so the caps do not reach VSQGs. Unconfirmed by a regulatory
            // specialist — flagged in the SME packet.
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        // An unknown status still gets the caps — the more protective default.
        return {
            kind: "VOLUME",
            nonAcuteGal: saaThresholds_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: saaThresholds_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: saaThresholds_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: saaThresholds_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "40 CFR 262.15",
            jurisdiction: "FEDERAL",
        };
    }
    satelliteClock(_ctx) {
        // 262.15 sets no standing clock on satellite accumulation — the only time
        // limit is the exceedance-triggered move window, carried on the caps.
        return { kind: "NO_LIMIT", citation: "40 CFR 262.15", jurisdiction: "FEDERAL" };
    }
}
exports.FederalRules = FederalRules;
/**
 * Behavior inherits; the spec never does. A state overrides only the slots
 * where its law departs from federal — inherited slots return FEDERAL-stamped
 * answers, the honest reading of "this state falls back to federal law here"
 * — but it must author its own complete expectations table, including rows
 * claiming those inherited federal answers. Were `expectations` inherited
 * too, a half-written state would pass the sweep with federal rows
 * masquerading as reviewed state claims.
 */
class StateRules extends FederalRules {
}
exports.StateRules = StateRules;
//# sourceMappingURL=FEDERAL.js.map